/**
 * Who opened a piece, and how far they read. For every masterclass, article
 * and lesson, public or members only, because they all render through
 * MasterclassReader and that is where this is called. A new piece is tracked
 * by being published; nobody has to remember to add anything.
 *
 * Why it exists: the NBA player development article went out as a public
 * link and there was no way to say how many people opened it. Plausible saw
 * every hash route as "/", the reader only logged a page as read when the
 * coach pressed Next (a one page article has no Next), and the email gate
 * only appears from page two. So: one event when it opens, and one at each
 * quarter of the way through.
 *
 *   masterclass_view   { module_key, public, layout, ref, utm_* }
 *   masterclass_read   { module_key, public, depth: 25 | 50 | 75 | 100 }
 *
 * Both go through logEvent, which writes Plausible and analytics_events, so
 * they are in the Plausible dashboard (as goals) and queryable in SQL.
 *
 * How far through is the reader's own measure: whole pages done plus the
 * scroll position on this one, over the page count. A page short enough to
 * fit the screen counts as read to the bottom, or a two paragraph lesson
 * could never reach 100.
 *
 * No em dashes.
 */
import { useEffect, useRef } from "react";
import { logEvent } from "../utils/analytics";

export const READ_DEPTHS = [25, 50, 75, 100];

/** The milestones `fraction` has reached that are not in `sent` yet. */
export function depthsReached(fraction, sent = []) {
  const pct = Math.max(0, Math.min(1, Number(fraction) || 0)) * 100;
  const done = new Set(sent);
  // 99.5 rather than 100: the last pixel of a scroll is often a rounding
  // error short of the bottom, and nobody reads to the 100th percentile.
  return READ_DEPTHS.filter(d => !done.has(d) && pct >= (d === 100 ? 99.5 : d));
}

/** Where the visitor came from, for the view event. Hash routes carry their
 *  own query string, so both it and the real one are read. */
export function arrivalProps(loc = typeof window !== "undefined" ? window.location : null,
  referrer = typeof document !== "undefined" ? document.referrer : "") {
  const out = {};
  try {
    const qs = [loc?.search || "", (loc?.hash || "").split("?")[1] || ""].join("&");
    const p = new URLSearchParams(qs);
    for (const k of ["utm_source", "utm_medium", "utm_campaign"]) {
      const v = p.get(k);
      if (v) out[k] = v.slice(0, 80);
    }
    if (referrer) {
      const host = new URL(referrer).hostname;
      if (host && host !== loc?.hostname) out.ref = host;
    }
  } catch { /* a malformed referrer is not worth an error */ }
  return out;
}

export function useReadTracking({ moduleKey, publicMode = false, layout, active = 0, total = 1, scrollRef }) {
  const sent = useRef([]);

  // One view per open.
  useEffect(() => {
    if (!moduleKey) return;
    sent.current = [];
    logEvent("masterclass_view", {
      module_key: moduleKey, public: !!publicMode, layout: layout || "chapters",
      ...arrivalProps(),
    });
  }, [moduleKey, publicMode, layout]);

  // Depth, from the scroll position and the page.
  useEffect(() => {
    const el = scrollRef && scrollRef.current;
    if (!moduleKey || !el) return;
    let raf = 0;
    const check = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const room = el.scrollHeight - el.clientHeight;
        const page = room > 40 ? Math.min(1, Math.max(0, el.scrollTop / room)) : 1;
        const pages = Math.max(1, Number(total) || 1);
        const fraction = (Math.min(active, pages - 1) + page) / pages;
        for (const depth of depthsReached(fraction, sent.current)) {
          sent.current = [...sent.current, depth];
          logEvent("masterclass_read", { module_key: moduleKey, public: !!publicMode, depth });
        }
      });
    };
    // Measure once the page has laid out, then on every scroll.
    const t = setTimeout(check, 400);
    el.addEventListener("scroll", check, { passive: true });
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", check);
    };
  }, [moduleKey, publicMode, active, total, scrollRef]);
}
